import {
  CONTENT_FULFILLED,
  FETCH_PRODUCTS_FULFILLED,
  SLICE_CATEGORY_CHANGED,
  SLICE_SUBCATEGORY_CHANGED,
} from '../actions';
import get from '../helpers/get';
import mergeSliceState from '../helpers/mergeSliceState';
import stringToArray from '../helpers/stringToArray';
import sanitizeFilterValue from '../helpers/sanitizeFilterValue';
import isCategoryFilter from '../helpers/isCategoryFilter';

const initialState = {};

const normalizeCategory = category => (
  (category && category.replace(/[^a-zA-Z0-9]/, '').toLowerCase()) || undefined
);

const getItemIdSelectorForSlice = sliceContent => {
  switch (sliceContent.type) {
    case 'catalog_tray':
      return item => Object
        .entries(item)
        .reduce((filters, [key, value]) => {
          const filerName = key.replace('filter_', '');
          if (['category', 'subcategory'].includes(key) || !value) {
            return filters;
          }
          return [...filters, `${filerName}=${sanitizeFilterValue(value, filerName)}`];
        }, []);
    case 'retail_tray':
      return ({ sku }) => stringToArray(sku);
    default:
      return (_, i) => i;
  }
};

const reduceItemsWithIds = (sliceContent, iterator, initialValue) => {
  const itemIdSelector = getItemIdSelectorForSlice(sliceContent);
  return (sliceContent.items || []).reduce((previousValue, item, i) => {
    const itemId = itemIdSelector(item, i);
    if (itemId !== undefined) {
      return iterator(previousValue, item, itemId);
    }
    return previousValue;
  }, initialValue);
};

const addContentsToArray = (itemIds, itemId) => {
  if (Array.isArray(itemId)) {
    itemIds.splice(itemIds.length, 0, ...itemId);
  } else if (itemId !== null && itemId !== undefined) {
    itemIds.push(itemId);
  }
  return itemIds;
};

/**
 * Returns an object, keyed by category title, of objects keyed by subcategory title, of arrays of
 * item identifiers. Items without a category are grouped under the `undefined` category, and items
 * without a subcategory are grouped under an `undefined` subcategory. The first occurrence of a
 * category or subcategory title is taken as the "canonical" title (i.e. capitalisation,
 * punctuation and whitespace), and other items are grouped with it even if their capitalisation,
 * punctuation or whitespace varies.
 */
const categorizeItems = sliceContent => {
  const categoryTitles = {}; // these objects are just to keep track of canonical titles
  const subcategoryTitles = {};
  return reduceItemsWithIds(sliceContent, (categorized, item, itemId) => {
    const result = categorized;
    const { category, subcategory } = item;
    const categoryKey = normalizeCategory(category);
    let categoryTitle;
    if (categoryKey) {
      categoryTitle = categoryTitles[categoryKey] || (categoryTitles[categoryKey] = category);
    }
    // Note: a sku can't be under a subcategory unless it is also under a category
    const subcategoryKey = categoryKey && normalizeCategory(subcategory);
    let subcategoryTitle;
    if (subcategoryKey) {
      subcategoryTitle = subcategoryTitles[subcategoryKey]
        || (subcategoryTitles[subcategoryKey] = subcategory);
    }
    const subcategories = result[categoryTitle] || (result[categoryTitle] = {});
    const itemIds = subcategories[subcategoryTitle] || (subcategories[subcategoryTitle] = []);

    addContentsToArray(itemIds, itemId);
    return result;
  }, {});
};

const getInitialCategory = (categories = {}) => Object.keys(categories)
  .find(title => title !== 'undefined');

const initializeSlice = sliceContent => {
  const categorized = sliceContent.categorised !== 'No';
  const categories = categorized ? categorizeItems(sliceContent) : {
    undefined: {
      undefined: sliceContent.items
        .reduce((ids, item, index) => addContentsToArray(
          ids,
          getItemIdSelectorForSlice(sliceContent)(item, index),
        ), []),
    },
  };
  const category = getInitialCategory(categories);
  return {
    categories,
    category,
    subcategory: getInitialCategory(categories[category]),
  };
};

const initializeRelevantSlices = (prevState, content) => ({
  ...(content.body || []).reduce((nextSlices, sliceContent) => {
    if (['content_tray', 'retail_tray', 'catalog_tray'].includes(sliceContent.type)) {
      return {
        ...nextSlices,
        [sliceContent.sliceId]: initializeSlice(sliceContent),
      };
    }
    return nextSlices;
  }, prevState),
});

const pruneMissingProducts = (state, sliceId, { products }) => {
  const sliceState = state[sliceId];
  if (sliceState) {
    // build a sku lookup table
    const validSkus = products.reduce((keyedBySku, { sku }) => ({ ...keyedBySku, [sku]: true }), {});
    const { categories } = sliceState;
    return mergeSliceState(state, sliceId, {
      categories: Object.keys(categories).reduce((processedCategories, category) => {
        const subCategories = categories[category];
        return {
          ...processedCategories,
          [category]: Object.keys(subCategories).reduce((processedSubCategories, subCategory) => ({
            ...processedSubCategories,
            // here is where the magic happens ... filter out the missing skus
            [subCategory]: subCategories[subCategory]
              .filter(id => isCategoryFilter(id) || validSkus[id]),
          }), {}),
        };
      }, {}),
    });
  }
  return state;
};

export default (state = initialState, action) => {
  switch (action.type) {
    case CONTENT_FULFILLED:
      return initializeRelevantSlices(state, action.payload);
    case FETCH_PRODUCTS_FULFILLED:
      return pruneMissingProducts(state, action.meta.sliceId, action.payload);
    case SLICE_CATEGORY_CHANGED:
      return mergeSliceState(state, action.meta.sliceId, {
        category: action.payload.category,
        subcategory: getInitialCategory(
          get(state, [action.meta.sliceId, 'categories', action.payload.category]),
        ),
      });
    case SLICE_SUBCATEGORY_CHANGED:
      return mergeSliceState(state, action.meta.sliceId, action.payload);
    default:
      return state;
  }
};
